import { COMMA, ENTER, PERIOD } from '@angular/cdk/keycodes';
import { DatePipe, registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faAddressBook } from '@fortawesome/free-solid-svg-icons';
import { ThemesService } from '@hub4b/services/core/themes.service';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { FileUploadModule } from 'ng2-file-upload';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { DialogContactsData } from 'src/app/components/dialogs/contacts/models/dialog-contacts-data';
import { Sms } from 'src/app/models/server/sms';
import { SmsTemplate } from 'src/app/models/server/sms-template';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { NotificationService } from 'src/app/services/actions/notification.service';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { FoldersService } from 'src/app/services/http/folders.service';
import { SmsTemplatesService } from 'src/app/services/http/sms-templates.service';
import { SmsService } from 'src/app/services/http/sms.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { ViewsStateService } from 'src/app/services/state/views-state.service';
import { ContactDataType } from '../contacts/models/contact-data-type';
import { DialogSendSmsData } from './models/dialog-send-sms-data';

registerLocaleData(localeFr);

@Component({
    selector: 'app-send-sms',
    templateUrl: './send-sms.component.html',
    styleUrls: ['./send-sms.component.scss'],
    imports: [
        MatCardModule,
        MatFormFieldModule,
        MatSelectModule,
        MatTooltipModule,
        FormsModule,
        MatOptionModule,
        NgxSpinnerModule,
        MatButtonModule,
        FaIconComponent,
        FileUploadModule,
        ReactiveFormsModule,
        MatChipsModule,
        MatIconModule,
        MatInputModule,
        AngularEditorModule,
    ],
})
export class SendSmsComponent implements OnInit, OnDestroy {
    /**
     * Références des icônes utilisés dans le template.
     */
    contactsBookIcon = faAddressBook;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Liste des modèles de SMS de l'organisation.
     */
    public templates: SmsTemplate[] = [];

    /**
     * Identifiant du modèle de sms sélectionné (0 indique aucun modèle de mail sélectionné).
     */
    public selectedTemplateId: string = '0';

    /**
     * Instance du SMS en cours d'edition.
     */
    public sms: Sms;

    /**
     * Expression régulière utilisée pour valider le format d'un numéro de téléphone.
     */
    public smsPattern = new RegExp(/^(0|\+33|0033)[1-9][0-9]{8}$/u);

    /**
     * Valeur indiquant si le format du champ 'destinataires' est valide.
     */
    public isRecipientsFormatValid: boolean = true;

    /**
     * Champs du formulaire d'envoi de mail.
     */
    public recipientsFormControl: UntypedFormControl = null;
    public contentFormControl: UntypedFormControl = null;
    public recipientInputFormControl: UntypedFormControl = null;

    /**
     * Touches utilisées pour valider la saisie d'un n° de téléphone.
     * PERIOD: point-virgule
     */
    readonly separatorKeysCodes: number[] = [ENTER, COMMA, PERIOD];

    /**
     * Taille maximale d'un SMS (160 caractères).
     */
    private smsMaxLength = 160;

    /**
     * Nombre maximal de SMS (10).
     */
    private smsMaxParts = 10;

    @ViewChild('progressBar', { read: ElementRef, static: true })
    progressBar: ElementRef;

    @ViewChild('charsCounter', { read: ElementRef, static: true })
    charsCounter: ElementRef;

    /**
     * Constructeur.
     * @param connectedUserStateService
     * @param mailtemplateService
     * @param dialogService
     * @param dialogRef
     * @param data
     */
    constructor(
        private connectedUserStateService: ConnectedUsersStateService,
        private viewsStateService: ViewsStateService,
        private smstemplateService: SmsTemplatesService,
        private smsService: SmsService,
        public folderService: FoldersService,
        private dialogService: DialogService,
        private notificationService: NotificationService,
        private spinner: SpinnerService,
        public dialogRef: MatDialogRef<SendSmsComponent>,
        private datePipe: DatePipe,
        private themesService: ThemesService,
        @Inject(MAT_DIALOG_DATA) public data: DialogSendSmsData
    ) {}

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.sms = {
            to: [],
            content: '',
        };

        this.recipientsFormControl = new UntypedFormControl(null, [Validators.required, MaxRecipientsValidator(this.sms)]);
        this.contentFormControl = new UntypedFormControl(null, [Validators.required]);
        this.recipientInputFormControl = new UntypedFormControl(null);

        this.contentFormControl.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
            this.displayProgressBar(value);
        });

        this.loadTemplates();
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Appelée lorsque l'utilisateur valide la saisie de n° de téléphone avec la touche ENTREE, virgule, point-virgule ou en quittant le champ de saisie.
     * @param event : Événement émis par le composant de saisie de n° de téléphone.
     */
    public addMobileNumber(event: MatChipInputEvent): void {
        const input = event.chipInput.inputElement;
        const value = event.value.trim();

        if (value !== '') {
            if (!!value && this.smsPattern.test(value) === true) {
                input.value = '';

                this.sms.to.push(value);
                this.isRecipientsFormatValid = true;
                this.recipientsFormControl.setErrors(null);
                this.recipientsFormControl.markAsPristine();
                this.recipientsFormControl.markAsUntouched();
            } else {
                this.isRecipientsFormatValid = false;
                this.recipientsFormControl.setErrors({ invalidFormat: true });
            }
        } else {
            this.isRecipientsFormatValid = false;
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur l'icône de suppression d'un n° de téléphone.
     * @param smsNumber : n° de téléphone à supprimer
     */
    public removeMobileNumber(smsNumber: string): void {
        this.sms.to.splice(this.sms.to.indexOf(smsNumber), 1);
        this.recipientsFormControl.updateValueAndValidity();
        if (this.sms.to.length == 0) {
            // Necessaire pour déclencher le validateur Required.
            this.recipientsFormControl.setValue('');
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur l'icône des contacts.
     */
    public displayContacts(): void {
        this.dialogService
            .openContacts(ContactDataType.Sms)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x: DialogContactsData) => {
                if (!!x) {
                    x.toAddresses.forEach((to) => {
                        this.sms.to.push(to);
                    });
                }
            });
    }

    /**
     * Appelée lorsque l'utilisateur sélectionne un modèle de sms.
     * @param templateId : Identifiant du modèle de sms sélectionné.
     */
    public applyTemplate(templateId: number): void {
        if (templateId != 0) {
            this.smstemplateService.get(templateId, this.data.folderId).subscribe((x) => {
                this.spinner.show('send-sms-spinner');

                this.sms.to = [];
                this.recipientInputFormControl.setValue('');

                if (!!x.template.to) {
                    x.template.to.forEach((to) => {
                        this.sms.to.push(to);
                    });

                    this.recipientsFormControl.setValue(this.sms.to);
                }
                this.recipientsFormControl.markAsDirty();
                this.recipientsFormControl.markAsTouched();
                this.recipientsFormControl.setErrors(null);
                this.recipientsFormControl.updateValueAndValidity();

                this.sms.content = x.template.content;
                this.contentFormControl.setValue(this.sms.content);
                this.contentFormControl.markAsDirty();
                this.contentFormControl.markAsTouched();
                this.contentFormControl.setErrors(null);
                this.contentFormControl.updateValueAndValidity();
                this.spinner.hide('send-sms-spinner');
            });
        } else {
            this.sms.to = [];
            this.recipientsFormControl.setValue(this.sms.to);
            this.recipientsFormControl.markAsDirty();
            this.recipientsFormControl.markAsTouched();
            this.recipientsFormControl.setErrors(null);
            this.recipientsFormControl.updateValueAndValidity();

            this.sms.content = '';
            this.contentFormControl.setValue(this.sms.content);
            this.contentFormControl.markAsDirty();
            this.contentFormControl.markAsTouched();
            this.contentFormControl.setErrors(null);
            this.contentFormControl.updateValueAndValidity();
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        this.dialogRef.close();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Valider'.
     */
    public send(): void {
        if (this.contentFormControl.valid && this.recipientsFormControl.valid) {
            this.spinner.show('send-sms-spinner');

            this.fillData();

            const user = this.connectedUserStateService.getUserConnected();

            this.smsService.sendSms(this.data.folderViewId, this.data.folderId, this.sms).subscribe({
                next: (x) => {
                    this.notificationService.displaySuccess('Envoi de SMS', 'Le SMS a été correctement envoyé.');
                    this.spinner.hide('send-sms-spinner');
                    this.dialogRef.close(x);
                },
                error: (x) => {
                    this.notificationService.displayError('Envoi de SMS', "Le SMS n'a pas pu être envoyé.");
                    this.spinner.hide('send-sms-spinner');
                },
            });
        } else {
            this.recipientsFormControl.markAsTouched();
            this.recipientsFormControl.updateValueAndValidity();
            this.contentFormControl.markAsTouched();
            this.contentFormControl.updateValueAndValidity();
        }
    }

    /**
     * Appellée lorsque l'utilisateur colle du texte dans le champ 'Destinataires'.
     * @param event Evenement copier/coller contenant les n° de telephone à ajouter au champ 'Destinataires'.
     */
    pasteMobileNumbers(event: ClipboardEvent): void {
        event.preventDefault();
        event.clipboardData
            .getData('Text')
            .split(/;|,|\n/)
            .forEach((value) => {
                if (value.trim() && this.smsPattern.test(value.trim()) === true) {
                    const smsNumber = this.smsPattern.exec(value.trim())[0];
                    this.sms.to.push(smsNumber);
                    this.isRecipientsFormatValid = true;
                }
            });
    }

    /**
     * Charge les modèles de SMS en mémoire.
     */
    private loadTemplates() {
        this.spinner.show('sms-template-spinner');
        this.smstemplateService
            .List(this.data.folderViewId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.templates = x;
                this.spinner.hide('sms-template-spinner');
            });
    }

    /**
     * Alimente une instance de la classe SMS avec les valeurs du formulaire.
     */
    private fillData() {
        this.sms.to = this.recipientsFormControl.value;
        this.sms.content = this.contentFormControl.value;
    }

    /**
     * Affiche la barre de progression indiquant le nombre de caractères saisis par rapport au nombre total de caractères acceptés
     * @param inputText : Texte saisi
     */
    private displayProgressBar(inputText: string) {
        let currentLength = inputText.length;

        // Si la longueur du texte saisi est supérieure à la taille maximale acceptée, on tronque la saisie à la taille maximale.
        if (currentLength > this.smsMaxLength * this.smsMaxParts) {
            this.contentFormControl.setValue((this.contentFormControl.value as string).substring(0, this.smsMaxLength * this.smsMaxParts));
            currentLength = this.smsMaxLength * this.smsMaxParts;
        }

        // Barre de progression
        let progressWidth = currentLength / (this.smsMaxLength * this.smsMaxParts) <= 1 ? (currentLength / (this.smsMaxLength * this.smsMaxParts)) * 100 : 100;
        this.progressBar.nativeElement.style.width = `${progressWidth}%`;
        this.charsCounter.nativeElement.style.visibility = 'visible';

        // Affiche une barre de progression colorée en fonction du nombre de caractères saisis.
        if (progressWidth <= 60) {
            this.progressBar.nativeElement.style.backgroundColor = this.themesService.getCurrentTheme() === ThemesService.DARK_THEME ? '#3dbd3d' : '#5bc25b';
        } else if (progressWidth > 60 && progressWidth < 90) {
            this.progressBar.nativeElement.style.backgroundColor = '#ec9d08';
        } else {
            this.progressBar.nativeElement.style.backgroundColor = '#fb5757';
        }

        // Cacul du nombre de caractères du SMS courant et du nombre de SMS.
        let currentSmsLenght = currentLength % this.smsMaxLength;
        let smsPartsCount = Math.floor(currentLength / this.smsMaxLength + 1);

        if (currentSmsLenght === 0 && smsPartsCount === 11) {
            currentSmsLenght = this.smsMaxLength;
            smsPartsCount = this.smsMaxParts;
        }

        // Affichage du compteur de caractères.
        this.charsCounter.nativeElement.innerHTML = `caractères : ${currentSmsLenght} / ${this.smsMaxLength} - SMS : ${smsPartsCount} / ${this.smsMaxParts}`;
        this.charsCounter.nativeElement.style.visibility = 'visible';

        if (currentSmsLenght < 100) {
            this.charsCounter.nativeElement.style.color = this.themesService.getCurrentTheme() === ThemesService.DARK_THEME ? '#41f141' : '#5bc25b';
        } else if (currentSmsLenght < 140) {
            this.charsCounter.nativeElement.style.color = '#ec9d08';
        } else {
            this.charsCounter.nativeElement.style.color = '#fb5757';
        }
    }
}

export function MaxRecipientsValidator(sms: Sms): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!sms) {
            return null;
        }

        return sms.to.length > 5 ? { maxRecipients: true } : null;
    };
}
